<script setup lang="ts">
// src/SideWinder.Web/src/components/StrategyPanel.vue
//
// Task 8 (user-level strategy assignment SPA): the super-user-only
// "Strategy" tab. Container for three sub-panels wired to
// /api/admin/strategy/* — the strategy catalog, per-user assignments (with
// the switch-strategy modal), and the recent signal log. Renders nothing
// for a non-super-user (server-side every endpoint 404s to them anyway;
// this is belt-and-braces so the tab's content never even mounts).
//
// 2026-10-02 (paolo): sub-tabs Allocation (everything above, plus the
// Strategy Allocation card moved here from the Exec screen, at the bottom)
// and Prospects (the fund shortlist, PR 3), kept in the URL hash as
// #strategy/allocation | #strategy/prospects (Dashboard keeps the
// strategy/ sub-hash intact, like Admin's).
//
// 2026-10-31 (paolo): a first sub-tab Edge (#strategy/edge), the default,
// holding the edge map moved here from Risk -> Live vs backtest.
import { ref, onMounted, onUnmounted } from 'vue'
import { useAuth } from '../composables/useAuth'
import AllocationHero from './strategy/AllocationHero.vue'
import StrategyLibraryPanel from './strategy/StrategyLibraryPanel.vue'
import UserAssignmentsPanel from './strategy/UserAssignmentsPanel.vue'
import SignalLogPanel from './strategy/SignalLogPanel.vue'
import ProspectsPanel from './strategy/prospects/ProspectsPanel.vue'
import EdgePanel from './strategy/EdgePanel.vue'
import StrategyAllocationCard from './StrategyAllocationCard.vue'

const { isSuperUser } = useAuth()

type StrategySubTab = 'edge' | 'allocation' | 'prospects'
function subTabFromHash(): StrategySubTab {
  const h = window.location.hash.replace('#', '')
  return h === 'strategy/prospects' ? 'prospects' : h === 'strategy/allocation' ? 'allocation' : 'edge'
}
const subTab = ref<StrategySubTab>(subTabFromHash())
function selectSubTab(t: StrategySubTab) {
  subTab.value = t
  window.location.hash = `strategy/${t}`
}
function onStrategyHashChange() {
  if (window.location.hash.startsWith('#strategy')) subTab.value = subTabFromHash()
}
onMounted(() => window.addEventListener('hashchange', onStrategyHashChange))
onUnmounted(() => window.removeEventListener('hashchange', onStrategyHashChange))
</script>

<template>
  <div v-if="isSuperUser" class="strategy-panel">
    <h2>Strategy</h2>
    <nav class="strategy-subtabs" role="tablist" data-testid="strategy-subtabs">
      <button type="button" role="tab" :aria-selected="subTab === 'edge'"
              :class="['strategy-subtab', { active: subTab === 'edge' }]"
              data-testid="strategy-subtab-edge" @click="selectSubTab('edge')">Edge</button>
      <button type="button" role="tab" :aria-selected="subTab === 'allocation'"
              :class="['strategy-subtab', { active: subTab === 'allocation' }]"
              data-testid="strategy-subtab-allocation" @click="selectSubTab('allocation')">Allocation</button>
      <button type="button" role="tab" :aria-selected="subTab === 'prospects'"
              :class="['strategy-subtab', { active: subTab === 'prospects' }]"
              data-testid="strategy-subtab-prospects" @click="selectSubTab('prospects')">Prospects</button>
    </nav>

    <div v-if="subTab === 'edge'" class="strategy-section" data-testid="strategy-edge"><EdgePanel /></div>

    <div v-else-if="subTab === 'allocation'" class="strategy-section" data-testid="strategy-allocation">
      <AllocationHero />
      <StrategyLibraryPanel />
      <UserAssignmentsPanel />
      <SignalLogPanel />
      <StrategyAllocationCard />
    </div>

    <div v-else class="strategy-section" data-testid="strategy-prospects"><ProspectsPanel /></div>
  </div>
</template>

<style scoped>
.strategy-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: #c9d1d9;
  font-family: system-ui, -apple-system, sans-serif;
}
.strategy-panel h2 {
  margin: 0;
  font-size: 1.1rem;
  color: #e6edf3;
}
.strategy-subtabs { display: flex; gap: 0.5rem; border-bottom: 1px solid #21262d; }
.strategy-subtab { min-height: 44px; padding: 0 1rem; background: none; color: #8b949e; border: none; border-bottom: 2px solid transparent; cursor: pointer; }
.strategy-subtab.active { color: #c9d1d9; border-bottom-color: #58a6ff; }
.strategy-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
